import authSession from "../../lib/authSession";
import { API_URL } from "../../constants";
import { safeFetch, explainApiError, toDisplayString } from "../../utils/toDisplayString";

/**
 * Resource Person auth slice (Zustand).
 *
 * AUTH endpoints (login/refresh/logout/password) are cookie-based: they are
 * called with `credentials: "include"` and no bearer token. Every other
 * resource-person endpoint authenticates with `Authorization: Bearer <token>`,
 * where the access token comes from the auth response body and lives in memory.
 */

export const resourcePersonAuthInitialState = {
  isAuthenticated: false,
  user: null,
  loginLoading: false,
  loginError: null,
  logoutLoading: false,
  authActionLoading: false,
  authActionError: null,
};

function apiUrl(path) {
  if (!API_URL) throw new Error("REACT_APP_API_BASE_URL is not configured.");
  return `${API_URL.replace(/\/$/, "")}${path}`;
}

function authOptions(method = "GET", body, credentials = "omit") {
  const headers = { Accept: "application/json" };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  // AUTH endpoints are cookie-based — they never send a bearer token.
  const token = credentials === "include" ? null : authSession.getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  return {
    method,
    headers,
    credentials,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  };
}

async function request(path, options = {}) {
  const response = await safeFetch(
    apiUrl(path),
    authOptions(options.method, options.body, options.credentials)
  );
  const text = await response.text();
  const contentType = response.headers.get("content-type") || "";
  let data = {};
  if (text) {
    if (contentType.includes("application/json")) data = JSON.parse(text);
    else if (text.trim().startsWith("<")) throw new Error(`Resource person auth API returned HTML (${response.status}).`);
    else data = JSON.parse(text);
  }
  if (!response.ok || data.error || data.success === false || data.status === "error" || data.status === false) {
    throw new Error(explainApiError(response.status, data || {}, "Request failed"));
  }
  return data;
}

/**
 * Recursively search a parsed JSON response for a user object.
 * Looks for the largest object that has recognisable user fields
 * (first_name, id, email, name).
 */
function findUser(node, visited = new Set()) {
  if (node === null || typeof node !== "object" || Array.isArray(node)) return undefined;
  if (visited.has(node)) return undefined;
  visited.add(node);
  const userLike = node.first_name || node.last_name || node.id || node.name;
  if (
    userLike !== undefined &&
    (Object.prototype.hasOwnProperty.call(node, "first_name") ||
      Object.prototype.hasOwnProperty.call(node, "email") ||
      Object.prototype.hasOwnProperty.call(node, "name"))
  ) {
    return node;
  }
  for (const key of ["data", "user", "auth", "payload", "response", "body", "result", "resourcePerson"]) {
    const nested = node[key];
    if (nested && typeof nested === "object") {
      const found = findUser(nested, visited);
      if (found !== undefined) return found;
    }
  }
  for (const value of Object.values(node)) {
    if (value && typeof value === "object") {
      const found = findUser(value, visited);
      if (found !== undefined) return found;
    }
  }
  return undefined;
}

function normalizeUser(user) {
  if (!user || typeof user !== "object") return null;
  const name = user.name || [user.first_name, user.last_name].filter(Boolean).join(" ");
  return { ...user, name: name || user.email || "Resource Person" };
}

function persistUser(user) {
  if (!user) return;
  localStorage.setItem("email", user.email || localStorage.getItem("email") || "");
  localStorage.setItem("name", user.name || "Resource Person");
  localStorage.setItem("image", user.avatar || user.image || "");
}

/**
 * Action factory for the resource person auth slice.
 * Returns the auth actions to be spread onto the Zustand store.
 */
export function createResourcePersonAuthActions(set, get) {
  return {
    /** POST /api/resource-person/auth/login — cookie-based auth. */
    login: async (email, password) => {
      set({ loginLoading: true, loginError: null });
      try {
        const json = await request("/api/resource-person/auth/login", {
          method: "POST",
          body: { email, password },
          // AUTH endpoint → cookies only.
          credentials: "include",
        });

        // If the backend explicitly says this account is not a lecturer,
        // surface that message.
        if (json.isLecturer === false) {
          const msg = "This account does not have tutor access. Please use the student login.";
          set({ loginLoading: false, loginError: msg });
          return { success: false, error: msg };
        }

        const user = normalizeUser(findUser(json));
        const token = json.accessToken || json.token || findToken(json);
        const refreshToken = json.refreshToken || json.refresh_token || null;
        // Always register the session: the role is read from the token, and a
        // cookie-only login response must still mark us as signed in.
        authSession.setSession({ token: token || null, refreshToken, role: "resource-person", user });
        persistUser(user);
        set({
          loginLoading: false,
          loginError: null,
          isAuthenticated: true,
          user: user || null,
        });
        return { success: true, user: user || null };
      } catch (err) {
        const message = toDisplayString(err.message, "Login failed");
        set({ loginLoading: false, loginError: message });
        return { success: false, error: message };
      }
    },

    /** POST /api/resource-person/auth/logout — clears the httpOnly cookie. */
    logout: async () => {
      set({ logoutLoading: true });
      try {
        await request("/api/resource-person/auth/logout", {
          method: "POST",
          credentials: "include",
        });
      } catch (err) {
        console.error("Resource person logout error:", err);
      } finally {
        authSession.clearSession();
        set({
          logoutLoading: false,
          isAuthenticated: false,
          user: null,
          loginError: null,
        });
      }
    },

    // Refresh uses the httpOnly refresh cookie via authSession.
    refresh: async () => {
      try {
        await authSession.refresh("resource-person");
        set({ isAuthenticated: true, authActionError: null });
        return { success: true };
      } catch (err) {
        const message = toDisplayString(err.message, "Failed to refresh session");
        set({ authActionError: message });
        return { success: false, error: message };
      }
    },
    fetchMe: async () => {
      set({ authActionLoading: true, authActionError: null });
      try {
        const data = await request("/api/resource-person/me");
        const user = normalizeUser(findUser(data) || data?.user || data?.data || null);
        persistUser(user);
        set({ authActionLoading: false, user, isAuthenticated: true });
        return { success: true, user };
      } catch (error) {
        return authActionError(set, error);
      }
    },
    updateMe: async (payload) => {
      set({ authActionLoading: true, authActionError: null });
      try {
        // PATCH /api/resource-person/me { name?, phone?, bio?, avatar? }
        const data = await request("/api/resource-person/me", { method: "PATCH", body: payload });
        const user = normalizeUser(findUser(data) || data?.user || data?.data || null);
        persistUser(user);
        set((state) => ({ authActionLoading: false, user: user || state.user }));
        return { success: true, user: user || data };
      } catch (error) {
        return authActionError(set, error);
      }
    },
    forgotPassword: async (email) => runAuthAction(set, "/api/resource-person/auth/forgot-password", "POST", { email }),
    resetPassword: async (payload) => runAuthAction(set, "/api/resource-person/auth/reset-password", "POST", payload),
    // POST, not PATCH: verified against the live API — PATCH …/change-password
    // answers 404 (method mismatch) while POST answers 401 (route exists).
    changePassword: async (payload) => runAuthAction(set, "/api/resource-person/auth/change-password", "POST", payload),

    /** Clear auth state locally (no server call). */
    clearAuth: () => {
      set({
        isAuthenticated: false,
        user: null,
        loginError: null,
      });
    },
  };
}

function findToken(node, visited = new Set()) {
  if (!node || typeof node !== "object" || visited.has(node)) return undefined;
  visited.add(node);
  for (const key of ["token", "accessToken", "access_token", "authToken", "jwt"]) {
    if (typeof node[key] === "string" && node[key]) return node[key];
  }
  for (const value of Object.values(node)) {
    const token = findToken(value, visited);
    if (token) return token;
  }
  return undefined;
}

async function runAuthAction(set, path, method, body) {
  set({ authActionLoading: true, authActionError: null });
  try {
    // AUTH endpoint → cookies only.
    const data = await request(path, { method, body, credentials: "include" });
    const accessToken = data.accessToken || data.token || findToken(data);
    if (accessToken) authSession.setSession({ token: accessToken, role: "resource-person" });
    set({ authActionLoading: false });
    return { success: true, data };
  } catch (error) {
    return authActionError(set, error);
  }
}

function authActionError(set, error) {
  const message = toDisplayString(error.message, "Request failed");
  set({ authActionLoading: false, authActionError: message });
  return { success: false, error: message };
}

export const resourcePersonAuthActions = createResourcePersonAuthActions;
export default createResourcePersonAuthActions;